import { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Empty,
  Form,
  Input,
  message,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";

import AppContext from "../../../../context/AppContext";
import { SIDER_TABS } from "../../../../utils/Constants";
import { useCasinoConfigurationProviders } from "../../../../queries/casino-configuration/providers/useCasinoConfigurationProviders";
import { useCasinoConfigurationDiscoverProviders } from "../../../../queries/casino-configuration/providers/useCasinoConfigurationDiscoverProviders";
import { useSaveCasinoConfigurationProvider } from "../../../../queries/casino-configuration/providers/useSaveCasinoConfigurationProvider";
import {
  useRemoveCasinoProviderTag,
  useSetCasinoProviderTags,
} from "../../../../queries/casino-configuration/providers/useSetCasinoProviderTags";
import { useAdminTags } from "../../../../queries/casino-configuration/games/useAdminTags";
import {
  buildS3UrlFromPresigned,
  uploadCasinoImageToS3,
} from "../../../../utils/casinoImageUpload";
import { useCanUploadCasinoFmV3Assets } from "../../../../utils/casinoFmPermissions";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import fallbackProviderIcon from "../../../../assets/icons/ic_casino.svg";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import DiscoverProvidersPage, {
  type DiscoverProviderRow,
} from "./pages/DiscoverProvidersPage";
import AddNewProviderPage from "./pages/AddNewProviderPage";
import TagModal from "../games/pages/TagModal";
import { useCurationDisplayName } from "../../../../hooks/useCurationDisplayName";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";

import "./stylesProvidersConfiguration.css";

type ProviderTag = {
  tag_id: string;
  name: string;
  name_translations?: Record<string, string>;
  color?: string;
};

type ProviderRow = {
  key: string;
  providerId: string;
  subprovider: string;
  name: string;
  iconUrl: string;
  isActive: boolean;
  gameCount: number;
  parentProvider?: string;
  tags: ProviderTag[];
};

type ProviderFormState = {
  providerId?: string;
  subprovider?: string;
  name: string;
  iconUrl: string;
  active: boolean;
};

const ACCEPTED_ICON_TYPES = ["image/png", "image/svg+xml", "image/webp"];
const MAX_ICON_BYTES = 5 * 1024 * 1024;

const normalizeText = (value: unknown) => String(value ?? "").trim().toLowerCase();

const resolveProviderIcon = (provider: Record<string, unknown>) =>
  String(
    provider.default_icon_url ||
      provider.icon_url ||
      provider.logo ||
      provider.image_url ||
      "",
  );

const isAcceptedImage = (file: File) => {
  const mimeOk = ACCEPTED_ICON_TYPES.includes(file.type);
  const extensionOk = /\.(png|svg|webp)$/i.test(file.name || "");
  return mimeOk || extensionOk;
};

/**
 * BE may return a provider's tags as an array of objects (`{tag_id, name,
 * color}`), an array of plain ids, or omit the field entirely on legacy
 * rows. Normalise to the FE-shaped `ProviderTag[]` so the row + modal
 * can rely on a single shape and treat missing/null as "no tag".
 */
const normalizeProviderTags = (raw: unknown): ProviderTag[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((tag): ProviderTag | null => {
      if (typeof tag === "string" && tag.trim()) {
        return { tag_id: tag, name: tag };
      }
      if (!tag || typeof tag !== "object") return null;
      const record = tag as Record<string, unknown>;
      const id = String(record.tag_id ?? record.id ?? record.name ?? "").trim();
      if (!id) return null;
      const name = String(record.name ?? id);
      const color = record.color ? String(record.color) : undefined;
      const translations = record.name_translations;
      const normalized: ProviderTag = { tag_id: id, name };
      if (color) normalized.color = color;
      if (
        translations &&
        typeof translations === "object" &&
        !Array.isArray(translations)
      ) {
        normalized.name_translations = translations as Record<string, string>;
      }
      return normalized;
    })
    .filter((t): t is ProviderTag => t !== null);
};


function ProvidersConfigurationPage() {
  usePageTitle(AppStrings.CASINO_CONFIG_PROVIDERS_TITLE);
  const queryClient = useQueryClient();
  const { setActiveSidebarTab } = useContext(AppContext);
  const canUploadCasinoFmV3Assets = useCanUploadCasinoFmV3Assets();
  const { getName: getTagDisplayName } = useCurationDisplayName();
  const [providersSearch, setProvidersSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [discoverSearch, setDiscoverSearch] = useState("");
  const [hideRegistered, setHideRegistered] = useState(true);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentFormState, setCurrentFormState] = useState<ProviderFormState>({
    name: "",
    iconUrl: "",
    active: true,
  });
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploadedIconUrl, setUploadedIconUrl] = useState<string | null>(null);
  const [originalIconUrl, setOriginalIconUrl] = useState("");
  const [togglingKeys, setTogglingKeys] = useState<Set<string>>(() => new Set());
  // `null` when no row's tag modal is open. We only ever show one tag
  // modal at a time and identify the row by its providerId so a refetch
  // (which produces new array references) doesn't break the binding.
  const [tagModalProviderId, setTagModalProviderId] = useState<string | null>(
    null,
  );
  const [form] = Form.useForm<{ name: string; active: boolean }>();
  const formActive = Form.useWatch("active", form);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(providersSearch.trim());
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [providersSearch]);

  const {
    data: providersData,
    isLoading: isLoadingProviders,
  } = useCasinoConfigurationProviders({ search: debouncedSearch });
  const {
    data: discoverData,
    isLoading: isLoadingDiscover,
    isError: isDiscoverError,
  } = useCasinoConfigurationDiscoverProviders({ enabled: discoverOpen });
  const { mutateAsync: saveProviderMutation } = useSaveCasinoConfigurationProvider();
  // Tag catalog + mutations. The catalog is fetched once and cached for
  // 5 minutes, so opening multiple rows in succession reuses one request.
  const { data: adminTags = [], isLoading: isLoadingTags } = useAdminTags();
  const { mutateAsync: setProviderTags } = useSetCasinoProviderTags();
  const { mutateAsync: removeProviderTag } = useRemoveCasinoProviderTag();

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "CONFIGURATION");
  }, [setActiveSidebarTab]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const providerRows: ProviderRow[] = useMemo(() => {
    const raw = Array.isArray(providersData) ? providersData : [];
    return raw.map((provider, idx) => {
      const record = provider as Record<string, unknown>;
      const providerId =
        String(record.provider_id || record.identifier || record.providerId || record.id || record.sub || "") ||
        `provider-${idx + 1}`;
      const subprovider = String(record.sub || record.provider_name || record.name || providerId);
      const gameCount = Number(record.game_count || record.games_count || 0);
      const isActive = record.is_active !== false;
      const parentName = record.parent_provider_name ? String(record.parent_provider_name) : "";
      return {
        key: `${providerId}-${subprovider}-${idx}`,
        providerId,
        subprovider,
        name: String(record.name || record.provider_name || record.sub || providerId),
        iconUrl: resolveProviderIcon(record),
        isActive,
        gameCount,
        tags: normalizeProviderTags(record.tags),
        ...(parentName ? { parentProvider: parentName } : {}),
      };
    });
  }, [providersData]);

  const providerStats = useMemo(() => {
    const registered = providerRows.length;
    const active = providerRows.filter((row) => row.isActive).length;
    const inactive = registered - active;
    return { registered, active, inactive };
  }, [providerRows]);


  const discoverRows: DiscoverProviderRow[] = useMemo(() => {
    const items = Array.isArray(discoverData) ? discoverData : [];

    const rows: DiscoverProviderRow[] = items.map(
      (item: Record<string, unknown>, idx: number) => {
        const providerId = String(
          item.provider_id || item.provider || item.identifier || "",
        );
        const sub = String(item.sub || item.name || providerId);
        const providerName = String(item.name || item.sub || providerId);
        const gameCount = Number(item.game_count || item.games_count || 0);
        const activeCount = Number(item.active_count || 0);
        const alreadyRegistered = item.sub_registered === true;

        return {
          key: `${normalizeText(providerId)}::${normalizeText(sub)}::${idx}`,
          providerId,
          providerName,
          subprovider: sub,
          iconUrl: resolveProviderIcon(item),
          gameCount,
          activeCount,
          alreadyRegistered,
        };
      },
    );

    const q = normalizeText(discoverSearch);
    return rows
      .filter((item) => {
        if (hideRegistered && item.alreadyRegistered) return false;
        if (!q) return true;
        return [item.providerName, item.subprovider, item.providerId]
          .map(normalizeText)
          .some((value) => value.includes(q));
      })
      .sort((a, b) => a.providerName.localeCompare(b.providerName));
  }, [discoverData, discoverSearch, hideRegistered]);

  const resetFormState = () => {
    form.resetFields();
    setUploadedIconUrl(null);
    setOriginalIconUrl("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setCurrentFormState({ name: "", iconUrl: "", active: true });
  };

  const openRegisterModal = (discover: DiscoverProviderRow) => {
    setOriginalIconUrl(discover.iconUrl || "");
    setUploadedIconUrl(null);
    setCurrentFormState({
      providerId: discover.subprovider || discover.providerId,
      subprovider: discover.subprovider,
      name: discover.providerName || discover.subprovider,
      iconUrl: discover.iconUrl || "",
      active: true,
    });
    form.setFieldsValue({
      name: discover.providerName || discover.subprovider,
      active: true,
    });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setRegisterOpen(true);
  };

  const openEditModal = (provider: ProviderRow) => {
    setOriginalIconUrl(provider.iconUrl || "");
    setUploadedIconUrl(null);
    setCurrentFormState({
      providerId: provider.providerId,
      subprovider: provider.subprovider,
      name: provider.name,
      iconUrl: provider.iconUrl || "",
      active: provider.isActive,
    });
    form.setFieldsValue({
      name: provider.name,
      active: provider.isActive,
    });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setEditOpen(true);
  };

  const beforeUpload = (file: File) => {
    if (!canUploadCasinoFmV3Assets) {
      message.error(AppStrings.PAM_UPLOADS_PERMISSION_REQUIRED);
      return false;
    }

    if (!isAcceptedImage(file)) {
      message.error(AppStrings.CASINO_CONFIG_PROVIDERS_UPLOAD_TYPE_ERROR);
      return false;
    }
    if (file.size > MAX_ICON_BYTES) {
      message.error(AppStrings.CASINO_CONFIG_PROVIDERS_UPLOAD_SIZE_ERROR);
      return false;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const nextPreview = URL.createObjectURL(file);
    setPreviewUrl(nextPreview);

    const identifier = currentFormState.providerId || "";

    uploadCasinoImageToS3({
      imageType: "provider_icon",
      identifier,
      contentType: file.type,
      file,
      existingUrl: uploadedIconUrl || originalIconUrl || undefined,
    })
      .then((presigned) => {
        const url = buildS3UrlFromPresigned(presigned);
        setUploadedIconUrl(url);
      })
      .catch((err: unknown) => {
        URL.revokeObjectURL(nextPreview);
        setPreviewUrl("");
        setUploadedIconUrl(null);
        message.error(
          getApiErrorMessage(
            err,
            AppStrings.CASINO_CONFIG_PROVIDERS_ICON_UPLOAD_FAILED,
          ),
        );
      });

    return false;
  };

  const removeIcon = () => {
    setUploadedIconUrl(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setCurrentFormState((prev) => ({ ...prev, iconUrl: "" }));
  };

  const saveProvider = async (mode: "create" | "update") => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const providerIdentifier =
        currentFormState.providerId ||
        values.name.trim().toLowerCase().replace(/\s+/g, "-");
      const iconUrl = uploadedIconUrl || currentFormState.iconUrl || "";
      await saveProviderMutation({
        mode,
        providerId: providerIdentifier,
        providerName: values.name.trim(),
        iconUrl,
        isActive: values.active,
      });
      message.success(
        mode === "create"
          ? AppStrings.CASINO_CONFIG_PROVIDERS_SAVE_CREATE_SUCCESS
          : AppStrings.CASINO_CONFIG_PROVIDERS_SAVE_UPDATE_SUCCESS,
      );

      await queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "providers"],
      });
      setRegisterOpen(false);
      setEditOpen(false);
      resetFormState();
    } catch (error) {
      if ((error as { errorFields?: unknown[] })?.errorFields) return;
      message.error(
        getApiErrorMessage(
          error,
          AppStrings.CASINO_CONFIG_PROVIDERS_SAVE_ERROR,
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const closeDiscoverModal = () => {
    setDiscoverOpen(false);
    setDiscoverSearch("");
    setHideRegistered(true);
  };

  const closeRegisterModal = () => {
    setRegisterOpen(false);
    resetFormState();
  };

  const closeEditModal = () => {
    setEditOpen(false);
    resetFormState();
  };

  const tagModalProvider = useMemo(
    () =>
      tagModalProviderId
        ? providerRows.find((row) => row.providerId === tagModalProviderId) ??
          null
        : null,
    [tagModalProviderId, providerRows],
  );

  const handleSetProviderTag = async (tagIds: string[]) => {
    if (!tagModalProvider) return;
    try {
      await setProviderTags({
        providerId: tagModalProvider.providerId,
        tagIds,
      });
      message.success(AppStrings.CASINO_CONFIG_GAMES_TAG_SET_SUCCESS);
      // Dismiss the modal on success so the admin lands back on the row
      // with the new badge applied — keeping it open after a successful
      // write requires an extra dismiss click that adds no value.
      setTagModalProviderId(null);
    } catch (err: unknown) {
      const e = err as { message?: string };
      message.error(
        e?.message || AppStrings.CASINO_CONFIG_GAMES_TAG_SET_ERROR,
      );
      throw err;
    }
  };

  const handleRemoveProviderTag = async (tagId: string) => {
    if (!tagModalProvider) return;
    try {
      await removeProviderTag({
        providerId: tagModalProvider.providerId,
        tagId,
      });
      // Distinct "Tag removed" feedback — the set-success string would
      // mislead admins who just cleared a tag into thinking they had
      // assigned one. Mirrors the collection-level handler's split.
      message.success(AppStrings.CASINO_CONFIG_COLLECTIONS_TAG_REMOVED);
      setTagModalProviderId(null);
    } catch (err: unknown) {
      const e = err as { message?: string };
      message.error(
        e?.message || AppStrings.CASINO_CONFIG_GAMES_TAG_SET_ERROR,
      );
      throw err;
    }
  };

  const handleToggleActive = async (provider: ProviderRow) => {
    const newActive = !provider.isActive;
    setTogglingKeys((prev) => new Set(prev).add(provider.key));
    try {
      const token = JSON.parse(localStorage.getItem("token") ?? "null");
      const instance = createPrivateAdminPanelInstance(token?.AccessToken);
      const url = API_ROUTES.UPDATE_ADMIN_PROVIDER.replace(
        "{identifier}",
        encodeURIComponent(provider.providerId),
      );
      await instance.patch(url, { is_active: newActive });
      await queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "providers"],
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      message.error(
        getApiErrorMessage(
          err,
          err?.message || AppStrings.PAM_GENERIC_REQUEST_FAILED,
        ),
      );
    } finally {
      setTogglingKeys((prev) => {
        const next = new Set(prev);
        next.delete(provider.key);
        return next;
      });
    }
  };

  return (
    <>
      <div className="casino-providers-shell">
        <div className="casino-providers-config">
          <div className="casino-providers-config-header">
            <Button
              className="casino-providers-primary-btn"
              icon={<PlusOutlined />}
              onClick={() => setDiscoverOpen(true)}
            >
              {AppStrings.CASINO_CONFIG_PROVIDERS_NEW_PROVIDER}
            </Button>
          </div>

          <div className="casino-providers-config-controls">
            <Input
              className="casino-providers-config-search"
              prefix={<SearchOutlined />}
              placeholder={AppStrings.CASINO_CONFIG_PROVIDERS_SEARCH_PLACEHOLDER}
              value={providersSearch}
              onChange={(event) => setProvidersSearch(event.target.value)}
              allowClear
            />
            <div className="casino-providers-inline-stats">
              <span className="casino-providers-stat">
                <b>{providerStats.registered}</b>{" "}
                {AppStrings.CASINO_CONFIG_PROVIDERS_REGISTERED}
              </span>
              <span className="casino-providers-stat">
                <b>{providerStats.active}</b> {AppStrings.CASINO_CONFIG_PROVIDERS_ACTIVE}
              </span>
              <span className="casino-providers-stat">
                <b>{providerStats.inactive}</b>{" "}
                {AppStrings.CASINO_CONFIG_PROVIDERS_INACTIVE}
              </span>
            </div>
          </div>

          <div className="casino-providers-list">
            {isLoadingProviders ? (
              <div className="casino-providers-loader">
                <RebetLoader height="88px" width="88px" />
              </div>
            ) : providerRows.length === 0 ? (
              <div className="casino-providers-empty">
                <Empty description={AppStrings.CASINO_CONFIG_PROVIDERS_EMPTY} />
              </div>
            ) : (
              providerRows.map((provider) => {
                // Schema allows multiple tags; product is one per provider.
                // Show the last attached tag so legacy multi-tag rows still
                // have a badge (the modal is where extras get cleaned up).
                const tag =
                  provider.tags.length > 0
                    ? provider.tags[provider.tags.length - 1]
                    : null;
                const tagLabel = tag ? getTagDisplayName(tag) : null;
                return (
                <div className="casino-providers-row" key={provider.key}>
                  <div className="casino-providers-row-left">
                    <img
                      className="casino-providers-config-logo"
                      src={provider.iconUrl || fallbackProviderIcon}
                      alt={provider.name}
                    />
                    <span className="casino-providers-row-name">
                      {provider.name}
                      {provider.providerId &&
                        provider.providerId !== provider.name && (
                          <span className="casino-providers-row-sub">
                            {provider.providerId}
                          </span>
                        )}
                      {provider.parentProvider &&
                        provider.parentProvider !== provider.providerId && (
                          <span className="casino-providers-row-via">
                            {AppStrings.CASINO_CONFIG_PROVIDERS_DISCOVER_VIA_PREFIX}{" "}
                            {provider.parentProvider}
                          </span>
                        )}
                    </span>
                  </div>

                  <div className="casino-providers-row-right">
                    <span className="casino-providers-games-count">
                      {provider.gameCount}{" "}
                      {AppStrings.CASINO_CONFIG_PROVIDERS_GAMES_SUFFIX}
                    </span>
                        <button
                          type="button"
                          className="casino-providers-tag-btn"
                          title={
                            tag && tagLabel !== tag.name ? tag.name : undefined
                          }
                          onClick={() =>
                            setTagModalProviderId(provider.providerId)
                          }
                          aria-label={`${
                            tag
                              ? formatString(
                                  AppStrings.CASINO_CONFIG_GAMES_TAG_MODAL_TITLE,
                                  { id: provider.providerId },
                                )
                              : AppStrings.CASINO_CONFIG_GAMES_TAG_ADD
                          } ${provider.name}`}
                          data-testid={`casino-providers-tag-btn-${provider.providerId}`}
                        >
                          {tag ? (
                            <span>
                              <span
                                className="casino-providers-tag-dot"
                                style={{ background: tag.color || "#9ea2b8" }}
                              />
                              {tagLabel}
                            </span>
                          ) : (
                            AppStrings.CASINO_CONFIG_GAMES_TAG_ADD
                          )}
                        </button>
                    <span
                      className={`casino-providers-active-label ${
                        provider.isActive
                          ? "casino-providers-active-label-on"
                          : "casino-providers-active-label-off"
                      }`}
                    >
                      {provider.isActive
                        ? AppStrings.CASINO_CONFIG_GAMES_STATUS_ON
                        : AppStrings.CASINO_CONFIG_GAMES_STATUS_OFF}
                    </span>
                    <label className="casino-providers-toggle-switch">
                      <input
                        type="checkbox"
                        checked={provider.isActive}
                        disabled={togglingKeys.has(provider.key)}
                        onChange={() => handleToggleActive(provider)}
                        aria-label={formatString(
                          AppStrings.CASINO_CONFIG_PROVIDERS_TOGGLE_ROW_ARIA,
                          { name: provider.name },
                        )}
                      />
                      <span className="casino-providers-toggle-slider" />
                    </label>
                    <button
                      className="casino-providers-edit-btn"
                      onClick={() => openEditModal(provider)}
                      aria-label={formatString(
                        AppStrings.CASINO_CONFIG_GAMES_EDIT_ARIA,
                        { name: provider.name },
                      )}
                      type="button"
                    >
                      <EditOutlined />
                    </button>
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <DiscoverProvidersPage
        open={discoverOpen}
        onCancel={closeDiscoverModal}
        discoverSearch={discoverSearch}
        onDiscoverSearchChange={setDiscoverSearch}
        hideRegistered={hideRegistered}
        onHideRegisteredChange={setHideRegistered}
        isLoadingGames={isLoadingDiscover}
        isError={isDiscoverError}
        discoverRows={discoverRows}
        totalCount={Array.isArray(discoverData) ? discoverData.length : 0}
        fallbackProviderIcon={fallbackProviderIcon}
        onRegisterClick={(row) => {
          closeDiscoverModal();
          openRegisterModal(row);
        }}
      />

      <AddNewProviderPage
        open={registerOpen}
        title={AppStrings.CASINO_CONFIG_PROVIDERS_MODAL_REGISTER_TITLE}
        submitText={AppStrings.CASINO_CONFIG_PROVIDERS_ACTION_REGISTER}
        onCancel={closeRegisterModal}
        onSubmit={() => saveProvider("create")}
        saving={saving}
        form={form}
        formActive={Boolean(formActive)}
        previewUrl={previewUrl}
        currentIconUrl={currentFormState.iconUrl}
        fallbackProviderIcon={fallbackProviderIcon}
        onBeforeUpload={beforeUpload}
        onRemoveIcon={removeIcon}
        canUploadImages={canUploadCasinoFmV3Assets}
        onActiveChange={(value) => form.setFieldValue("active", value)}
      />

      <AddNewProviderPage
        open={editOpen}
        title={AppStrings.CASINO_CONFIG_PROVIDERS_MODAL_EDIT_TITLE}
        submitText={AppStrings.CASINO_CONFIG_PROVIDERS_ACTION_UPDATE}
        onCancel={closeEditModal}
        onSubmit={() => saveProvider("update")}
        saving={saving}
        form={form}
        formActive={Boolean(formActive)}
        previewUrl={previewUrl}
        currentIconUrl={currentFormState.iconUrl}
        fallbackProviderIcon={fallbackProviderIcon}
        onBeforeUpload={beforeUpload}
        onRemoveIcon={removeIcon}
        canUploadImages={canUploadCasinoFmV3Assets}
        onActiveChange={(value) => form.setFieldValue("active", value)}
      />

      <TagModal
        open={tagModalProvider !== null}
        gameLabel={tagModalProvider?.subprovider ?? ""}
        currentTags={(tagModalProvider?.tags ?? []).map((t) => t.tag_id)}
        availableTags={adminTags}
        loading={isLoadingTags}
        onSetTag={handleSetProviderTag}
        onRemoveTag={handleRemoveProviderTag}
        onClose={() => setTagModalProviderId(null)}
      />
    </>
  );
}

export default ProvidersConfigurationPage;
